import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Papa from "papaparse";
import { Change, manResponseType } from "../../interfaces";
import {
  getOnRevisionChanges,
  approveChange,
  rejectChange,
  associateValueToRule,
} from "../../Services/ChangeService";
import EditingTable from "../EditingTable/EditingTable";
import { ChangeStateEnum, PhaseDictionary } from "../../Enum";
import { useAuth } from "../../Context/useAuth";
import { toast } from "react-toastify";

const useDebounce = <T,>(value: T, delay: number = 300): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const CheckRevision: React.FC = () => {
  const [changes, setChanges] = useState<Change[]>([]);
  const [filteredChanges, setFilteredChanges] = useState<Change[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  const [manifestNames, setManifestNames] = useState<Record<string, string>>({});
  
  const [searchPatientId, setSearchPatientId] = useState<string>("");
  const [searchManifestation, setSearchManifestation] = useState<string>("");
  const [searchPhase, setSearchPhase] = useState<string>("");
  
  const debouncedPatientId = useDebounce(searchPatientId, 300);
  const debouncedManifestation = useDebounce(searchManifestation, 300);
  const debouncedPhase = useDebounce(searchPhase, 300);

  const currentUser = useAuth().user;
  const [visibleChanges, setVisibleChanges] = useState<Change[]>([]);
  const [forceResetPage, setForceResetPage] = useState(false);

  const [responseDescriptions, setResponseDescriptions] = useState<manResponseType[]>([]);
  const [idManResponseAvailable, setIdManResponseAvailable] = useState<number[]>([]);

  const [resolvedValues, setResolvedValues] = useState<Record<string, string>>({});
  const [newIdManResponses, setNewIdManResponses] = useState<Record<string, number>>({});

  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [editedIds, setEditedIds] = useState<Set<string>>(new Set());
  
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    type: "approve" | "reject";
    id: string;
    message: string;
  } | null>(null);

  const [cacheStats, setCacheStats] = useState({ hits: 0, misses: 0 });

  type CacheValue = { label: string; idMan: number };
  const idManResponseCache = useRef<Map<string, CacheValue>>(new Map());
  const cacheKey = (phaseId: number, identification: string, mn: string) =>
    `${phaseId}:${identification}:${mn}`;

  const clearCache = useCallback(() => {
    idManResponseCache.current.clear();
    setCacheStats({ hits: 0, misses: 0 });
    toast.info("🧹 Caché limpiada");
  }, []);

  const computeFiltered = useCallback((
    changes: Change[],
    manifestNames: Record<string, string>,
    searchPatientId: string,
    searchManifestation: string,
    searchPhase: string
  ) => {
    if (!manifestNames || Object.keys(manifestNames).length === 0) return changes;

    return changes.filter((change) => {
      const manifestation = manifestNames[change.variableName] || change.variableName;
      const phase = PhaseDictionary[change.id_Phase] || change.id_Phase.toString();
      
      const matchesPatient = change.identification
        .toLowerCase()
        .includes(searchPatientId.toLowerCase());
      const matchesManifestation = manifestation
        .toLowerCase()
        .includes(searchManifestation.toLowerCase());
      const matchesPhase = searchPhase === "" || phase.includes(searchPhase);

      return matchesPatient && matchesManifestation && matchesPhase;
    });
  }, []);

  const prettyValue = (v: string) => {
    if (!v) return v;
    const iso = /^\d{4}-\d{2}-\d{2}T/;
    if (iso.test(v)) return v.split("T")[0];
    return v;
  };

  const setProcessing = (id: string, on: boolean) =>
    setProcessingIds((prev) => {
      const next = new Set(prev);
      on ? next.add(id) : next.delete(id);
      return next;
    });

  // Cargar CSV: response_description
  useEffect(() => {
    const fetchResponseDescription = async () => {
      try {
        const response = await fetch("/response_description.csv");
        const csvText = await response.text();

        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (result: any) => {
            const parsedData: manResponseType[] = [];

            result.data.forEach((row: any) => {
              if (
                row.Id &&
                row.Description &&
                row.ResponseType &&
                row.id &&
                row.description_es &&
                row.description_es !== "#ERROR!"
              ) {
                parsedData.push({
                  Id: Number(row.Id),
                  Description: row.Description,
                  ResponseType: row.ResponseType,
                  id: Number(row.id),
                  description_es: row.description_es,
                });
              }
            });
            setResponseDescriptions(parsedData);
            const uniqueIds = Array.from(new Set(parsedData.map((item) => item.Id)));
            setIdManResponseAvailable(uniqueIds);
          },
        });
      } catch (error) {
        console.error("Error al cargar response descriptions:", error);
        toast.error("❌ Error al cargar descripciones de respuestas");
      }
    };

    fetchResponseDescription();
  }, []);

  useEffect(() => {
    const fetchResolvedValues = async () => {
      if (
        visibleChanges.length === 0 ||
        responseDescriptions.length === 0 ||
        idManResponseAvailable.length === 0
      )
        return;

      const newMap: Record<string, string> = {};
      const newIdmanResponse: Record<string, number> = {};

      const changesToResolve = visibleChanges.filter(
        (change) => !(change.id in resolvedValues)
      );

      if (changesToResolve.length === 0) return;

      const concurrency = 6;
      const queue = [...changesToResolve];
      
      let localHits = 0;
      let localMisses = 0;

      const worker = async (change: Change) => {
        try {
          const key = cacheKey(change.id_Phase, change.identification, change.variableName);
          const cached = idManResponseCache.current.get(key);

          if (cached) {
            localHits++;
            newIdmanResponse[change.id] = cached.idMan;
            newMap[change.id] = prettyValue(cached.label);
            return;
          }

          localMisses++;

          if (change.variableName.endsWith("1")) {
            const label = prettyValue(change.oldValue);
            newIdmanResponse[change.id] = 0;
            newMap[change.id] = label;
            idManResponseCache.current.set(key, { label, idMan: 0 });
            return;
          }

          const [label, idMan] = await associateValueToRule(
            change.oldValue,
            change.variableName,
            [change.id_Phase],
            idManResponseAvailable,
            responseDescriptions
          );

          const finalLabel = prettyValue(label);
          newIdmanResponse[change.id] = idMan;
          newMap[change.id] = finalLabel;
          idManResponseCache.current.set(key, { label: finalLabel, idMan });
        } catch {
          const label = prettyValue(change.oldValue);
          newIdmanResponse[change.id] = 0;
          newMap[change.id] = label;
        }
      };

      const runners: Promise<void>[] = [];
      for (let i = 0; i < concurrency; i++) {
        const run = (async () => {
          while (queue.length) {
            const item = queue.shift();
            if (!item) break;
            await worker(item);
          }
        })();
        runners.push(run);
      }
      await Promise.all(runners);

      setNewIdManResponses((prev) => ({ ...prev, ...newIdmanResponse }));
      setResolvedValues((prev) => ({ ...prev, ...newMap }));
      setCacheStats((prev) => ({
        hits: prev.hits + localHits,
        misses: prev.misses + localMisses,
      }));
    };

    fetchResolvedValues();
  }, [visibleChanges, responseDescriptions, idManResponseAvailable, resolvedValues]);

  // Cargar CSV: nombre_manifestaciones
  useEffect(() => {
    const fetchManifestNames = async () => {
      try {
        const response = await fetch("/nombre_manifestaciones.csv");
        const csvText = await response.text();

        Papa.parse(csvText, {
          header: false,
          skipEmptyLines: true,
          complete: (result: any) => {
            const names: Record<string, string> = {};
            result.data.forEach((row: string[]) => {
              if (row[0] && row[1]) {
                names[row[0]] = row[1];
              }
            });
            setManifestNames(names);
          },
        });
      } catch (error) {
        console.error("Error al cargar manifestaciones:", error);
        toast.error("❌ Error al cargar nombres de manifestaciones");
      }
    };

    fetchManifestNames();
  }, []);

  useEffect(() => {
    setFilteredChanges(
      computeFiltered(
        changes,
        manifestNames,
        debouncedPatientId,
        debouncedManifestation,
        debouncedPhase
      )
    );
  }, [
    changes,
    manifestNames,
    debouncedPatientId,
    debouncedManifestation,
    debouncedPhase,
    computeFiltered,
  ]);

  const lastSearchRef = useRef({ pid: "", man: "", phase: "" });
  useEffect(() => {
    const last = lastSearchRef.current;
    if (
      last.pid !== debouncedPatientId ||
      last.man !== debouncedManifestation ||
      last.phase !== debouncedPhase
    ) {
      setForceResetPage(true);
      lastSearchRef.current = {
        pid: debouncedPatientId,
        man: debouncedManifestation,
        phase: debouncedPhase,
      };
    }
  }, [debouncedPatientId, debouncedManifestation, debouncedPhase]);

  useEffect(() => {
    if (forceResetPage) {
      setForceResetPage(false);
    }
  }, [filteredChanges, forceResetPage]);

  const fetchChanges = useCallback(async (showToast = true) => {
    try {
      setRefreshing(true);
      const result = await getOnRevisionChanges();
      setChanges(result);
      if (showToast) {
        toast.success(`✅ ${result.length} cambios cargados`);
      }
    } catch (error) {
      console.error("Error al obtener los cambios en revision:", error);
      toast.error("❌ Error al cargar cambios en revisión");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Carga inicial
  useEffect(() => {
    fetchChanges(false);
  }, [fetchChanges]);

  const changetableConfig = useMemo(
    () => (manifestNames: Record<string, string>) => [
      {
        key: "id_Patient",
        label: "Patient ID",
        render: (manifest: Change) => (
          <a
            href={`https://arthrosoft.com/es/Project/Project/PatientClinicHistory/${manifest.id_Patient}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
            title="Ver historia clínica (abre en nueva pestaña)"
          >
            {manifest.identification}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </a>
        ),
        subTitle: "Identificador del paciente (clic para ver historia clínica)",
      },
      {
        key: "id_Phase",
        label: "Phase",
        render: (manifest: Change) => (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            Fase {PhaseDictionary[manifest.id_Phase] || manifest.id_Phase}
          </span>
        ),
        subTitle: "Identificador de la fase",
      },
      {
        key: "variableName",
        label: "Manifestation",
        render: (manifest: Change) => (
          <span className="text-sm font-medium">
            {manifestNames[manifest.variableName] || manifest.variableName}
          </span>
        ),
        subTitle: "La manifestación a cambiar",
      },
      {
        key: "oldValue",
        label: "Current Value",
        render: (manifest: Change) => {
          const oldValue = prettyValue(manifest.oldValue);
          const resolved = resolvedValues[manifest.id]
            ? prettyValue(resolvedValues[manifest.id])
            : undefined;

          if (resolved && resolved !== oldValue) {
            return (
              <span className="text-sm">
                {resolved}{" "}
                <span className="text-gray-500 text-xs">({oldValue})</span>
              </span>
            );
          }
          return <span className="text-sm">{resolved || oldValue}</span>;
        },
        subTitle: "El valor anterior",
      },
      {
        label: "New Value",
        render: (manifest: Change, onEdit: (id: string, newValue: string) => void) => {
          const options = responseDescriptions.filter(
            (desc) => desc.Id === newIdManResponses[manifest.id]
          );

          if (options.length > 0) {
            return (
              <select
                value={manifest.newValue}
                onChange={(e) => onEdit(manifest.id, e.target.value)}
                className="border border-gray-300 p-2 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                title="Seleccionar nuevo valor"
              >
                <option value="">Seleccionar...</option>
                {options.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {`${opt.description_es} (${opt.id})`}
                  </option>
                ))}
              </select>
            );
          }

          return (
            <div className="flex flex-col gap-1">
              <input
                type="text"
                value={manifest.newValue}
                onChange={(e) => onEdit(manifest.id, e.target.value)}
                className="border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Ingresá un valor manualmente"
                title="Ingresá un valor manualmente"
              />
              <small className="text-gray-500 text-xs">
                💡 No hay valores predefinidos para esta manifestación/fase
              </small>
            </div>
          );
        },
        subTitle: "El nuevo valor",
      },
      {
        key: "comment",
        label: "Comment",
        render: (manifest: Change) => (
          <span className="text-sm text-gray-700">{manifest.comment || "-"}</span>
        ),
        subTitle: "Comentario del cambio",
      },
    ],
    [manifestNames, resolvedValues, newIdManResponses, responseDescriptions]
  );

  // Edición de celdas
  const handleEdit = useCallback((id: string, newValue: string) => {
    setChanges((prevChanges) =>
      prevChanges.map((change) => (change.id === id ? { ...change, newValue } : change))
    );
    setEditedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const validateChange = (change: Change): { valid: boolean; message: string } => {
    if (!change.newValue || change.newValue.trim() === "") {
      return { valid: false, message: "El nuevo valor no puede estar vacío" };
    }
    return { valid: true, message: "" };
  };

  const handleApprove = useCallback(async (id: string) => {
    const changeToApprove = changes.find((change) => change.id === id);
    if (!changeToApprove) return;

    // Validar antes de mostrar confirmación
    const validation = validateChange(changeToApprove);
    if (!validation.valid) {
      toast.error(`❌ ${validation.message}`);
      return;
    }

    setConfirmModal({
      show: true,
      type: "approve",
      id,
      message: `¿Confirmar aprobación del cambio para ${changeToApprove.identification}?`,
    });
  }, [changes]);

  const handleReject = useCallback(async (id: string) => {
    const changeToReject = changes.find((change) => change.id === id);
    if (!changeToReject) return;

    setConfirmModal({
      show: true,
      type: "reject",
      id,
      message: `¿Confirmar rechazo del cambio para ${changeToReject.identification}?`,
    });
  }, [changes]);

  const handleConfirm = async () => {
    if (!confirmModal) return;

    const { type, id } = confirmModal;
    const change = changes.find((c) => c.id === id);
    if (!change) return;

    setConfirmModal(null);
    setProcessing(id, true);

    try {
      if (type === "approve") {
        await approveChange(id, {
          id_Patient: change.id_Patient,
          id_Phase: change.id_Phase,
          variableName: change.variableName,
          identification: change.identification,
          dataEntryId: change.dataEntryId,
          newValue: change.newValue,
          changeDate: change.changeDate,
          changedBy: currentUser ? currentUser.userName : "System",
          changeState: ChangeStateEnum.ToSend,
        });
        toast.success(`✅ Cambio aprobado para ${change.identification}`);
      } else {
        await rejectChange(id, {
          id_Patient: change.id_Patient,
          id_Phase: change.id_Phase,
          variableName: change.variableName,
          identification: change.identification,
          dataEntryId: change.dataEntryId,
          newValue: change.newValue,
          changeDate: change.changeDate,
          changedBy: currentUser ? currentUser.userName : "System",
          changeState: ChangeStateEnum.Discarded,
        });
        toast.success(`✅ Cambio rechazado para ${change.identification}`);
      }

      setChanges((prevChanges) => prevChanges.filter((c) => c.id !== id));
      setEditedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (error) {
      console.error(`Error al ${type === "approve" ? "aprobar" : "rechazar"}:`, error);
      toast.error(`❌ Error al ${type === "approve" ? "aprobar" : "rechazar"} el cambio`);
    } finally {
      setProcessing(id, false);
    }
  };

  const handleCancel = () => {
    setConfirmModal(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        <span className="ml-3 text-lg">Cargando cambios en revisión...</span>
      </div>
    );
  }

  if (changes.length === 0 && !loading) {
    return (
      <div className="p-4">
        <div className="text-center py-12">
          <svg
            className="mx-auto h-24 w-24 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            No hay cambios en revisión
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            Todos los cambios han sido procesados o no hay cambios pendientes.
          </p>
          <button
            onClick={() => fetchChanges(true)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            🔄 Refrescar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 shadow-sm">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Revisión de Cambios</h2>
            <p className="text-sm text-gray-600 mt-1">
              📊 Mostrando {filteredChanges.length} de {changes.length} cambios
              {editedIds.size > 0 && (
                <span className="ml-2 text-yellow-600 font-medium">
                  • {editedIds.size} editado{editedIds.size > 1 ? "s" : ""}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Stats de caché */}
            {cacheStats.hits + cacheStats.misses > 0 && (
              <div className="text-xs text-gray-600 bg-white px-3 py-2 rounded-lg shadow">
                📦 Caché: {cacheStats.hits} hits / {cacheStats.misses} misses
                <button
                  onClick={clearCache}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                  title="Limpiar caché"
                >
                  🧹
                </button>
              </div>
            )}
            <button
              onClick={() => fetchChanges(true)}
              disabled={refreshing}
              className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow transition-all ${
                refreshing ? "opacity-50 cursor-not-allowed" : ""
              }`}
              title="Recargar cambios"
            >
              {refreshing ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Cargando...
                </span>
              ) : (
                "🔄 Refrescar"
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="mb-6 bg-white rounded-lg shadow p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">🔍 Filtros de Búsqueda</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Patient ID
            </label>
            <input
              type="text"
              placeholder="Ej: C10P14"
              value={searchPatientId}
              onChange={(e) => setSearchPatientId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Manifestación
            </label>
            <input
              type="text"
              placeholder="Ej: Edad, Peso..."
              value={searchManifestation}
              onChange={(e) => setSearchManifestation(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fase</label>
            <select
              value={searchPhase}
              onChange={(e) => setSearchPhase(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">Todas las fases</option>
              {Object.entries(PhaseDictionary).map(([key, value]) => (
                <option key={key} value={value}>
                  Fase {value}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Indicador de búsqueda activa */}
        {(searchPatientId || searchManifestation || searchPhase) && (
          <div className="mt-3 flex items-center gap-2 text-sm">
            <span className="text-gray-600">Filtros activos:</span>
            {searchPatientId && (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                Patient: {searchPatientId}
              </span>
            )}
            {searchManifestation && (
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                Manifest: {searchManifestation}
              </span>
            )}
            {searchPhase && (
              <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">
                Fase: {searchPhase}
              </span>
            )}
            <button
              onClick={() => {
                setSearchPatientId("");
                setSearchManifestation("");
                setSearchPhase("");
              }}
              className="text-red-600 hover:text-red-800 text-xs ml-2"
            >
              ✖ Limpiar filtros
            </button>
          </div>
        )}
      </div>

      {/* Tabla */}
      {filteredChanges.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <svg
            className="mx-auto h-16 w-16 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <p className="mt-4 text-gray-600">No se encontraron cambios con los filtros aplicados</p>
          <button
            onClick={() => {
              setSearchPatientId("");
              setSearchManifestation("");
              setSearchPhase("");
            }}
            className="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Limpiar filtros
          </button>
        </div>
      ) : (
        <EditingTable
          data={filteredChanges}
          configs={changetableConfig(manifestNames)}
          onApprove={handleApprove}
          onReject={handleReject}
          onEdit={handleEdit}
          onVisibleRowsChange={setVisibleChanges}
          resetPageTrigger={forceResetPage}
          processingIds={Array.from(processingIds)}
          editedIds={Array.from(editedIds)}
        />
      )}

      {confirmModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => e.target === e.currentTarget && handleCancel()}
        >
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
            <h3
              className={`text-xl font-bold mb-4 ${
                confirmModal.type === "approve" ? "text-green-600" : "text-red-600"
              }`}
            >
              {confirmModal.type === "approve" ? "✅ Confirmar Aprobación" : "❌ Confirmar Rechazo"}
            </h3>
            <p className="text-gray-700 mb-6">{confirmModal.message}</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirm}
                className={`px-4 py-2 text-white rounded-lg transition-colors ${
                  confirmModal.type === "approve"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {confirmModal.type === "approve" ? "Aprobar" : "Rechazar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckRevision;